import React, { useState, useMemo, useCallback, useRef } from "react";
import FilterBar from "./FilterBar";
import { formatUSD } from "@/utils/formatters";
import type { InstallmentLead } from "../../hooks/useOperationsAnalytics";
import type {
  InstallmentFilterColumn,
  FilterMode,
  InstallmentFilterChip,
} from "../../hooks/useInstallmentFilters";
import { getInstallmentColumnValue } from "../../hooks/useInstallmentFilters";
import type { InstallmentClaim } from "../../hooks/data-fetching/useInstallmentClaims";
import { toTitleCase } from "../../lib/titleCase";
import MiniSparkline from "./MiniSparkline";
import LeadTimelineModal from "../LeadTimelineModal";
import SingleDatePicker from "../ui/SingleDatePicker";
import { useScrollReveal } from "../../hooks/useScrollReveal";

// =============================================================================
// INSTALLMENTS TRACKER
// =============================================================================
// PURPOSE: Payment plan tracking table with sortable columns, click-to-filter,
// client-side pagination, "Closed after" filter, and per-row abandon button.
//
// MODES:
// - snippet=true  → 5 rows on OperationsDashboard, no filters/sort
// - snippet=false → Full table on InstallmentTrackingPage with all controls
//
// CLICK-TO-FILTER:
// - Left-click filterable cell  → isolate (show only matching rows)
// - Right-click filterable cell → exclude (hide matching rows)
// - Chips in FilterBar let you re-include excluded values
// - Filter state lives in parent (useInstallmentFilters at page level)
// =============================================================================

type SortKey =
  | "name"
  | "program"
  | "closer"
  | "setter"
  | "contract"
  | "collected"
  | "remaining"
  | "nextPayment"
  | "nextAmount"
  | "lastPayment"
  | "status";
type SortDir = "asc" | "desc";

const FILTERABLE_COLUMNS = new Set<InstallmentFilterColumn>([
  "closer",
  "setter",
  "program",
  "status",
]);

const PAGE_SIZE = 50;

interface InstallmentsTrackerProps {
  leads: InstallmentLead[];
  snippet?: boolean;
  timezone?: string;
  isAbandoned?: boolean;
  onConversationClick?: (
    conversationId: string,
    contactName: string,
    leadInstagram: string | null
  ) => void;
  onAbandonClick?: (lead: InstallmentLead) => void;
  onRestoreClick?: (lead: InstallmentLead) => void;
  canAbandon?: boolean;
  canRestore?: boolean;
  // Inactive-rep highlight (org-wide visibility can surface an orphaned plan)
  inactiveRepIds?: Set<string>;
  memberNameById?: Map<string, string>;
  // Claimable pool — self-claim an orphaned plan
  claimsByEmail?: Map<string, InstallmentClaim>;
  canClaimInstallment?: boolean;
  onClaimInstallment?: (leadEmail: string) => void;
  onReleaseInstallment?: (leadEmail: string) => void;
  // Snippet mode
  viewAllLink?: string;
  viewAllCount?: number;
  // Closed after filter (full mode)
  closedAfterDate?: string;
  onClosedAfterChange?: (date: string) => void;
  // Click-to-filter (full mode)
  filterChips?: InstallmentFilterChip[];
  onApplyFilter?: (column: InstallmentFilterColumn, value: string, mode: FilterMode) => void;
  onRemoveFilter?: (column: InstallmentFilterColumn, value: string) => void;
  onClearColumnFilter?: (column: InstallmentFilterColumn) => void;
  onClearAllFilters?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (d: string, timezone: string) => {
  if (!d) return "--";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: timezone,
  });
};

function getLeadStatus(lead: InstallmentLead): string {
  if (lead.isAbandoned) return "Abandoned";
  if (lead.isFullyPaid) return "Paid";
  if (!lead.nextPaymentDate) return "No date";
  if (lead.isOverdue || (lead.nextPaymentDate && new Date(lead.nextPaymentDate) < new Date()))
    return "Overdue";
  return "Active";
}

function statusBadge(status: string) {
  switch (status) {
    case "Overdue":
      return "bg-ds-red-bg text-ds-red";
    case "No date":
      return "bg-ds-amber-bg text-ds-amber";
    case "Paid":
      return "bg-ds-green-bg text-ds-green";
    case "Abandoned":
      return "bg-ds-red-bg text-ds-red";
    default:
      return "bg-ds-accent-light text-ds-accent";
  }
}

// ---------------------------------------------------------------------------
// SortHeader
// ---------------------------------------------------------------------------

const SortHeader: React.FC<{
  label: string;
  field: SortKey;
  className?: string;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (field: SortKey) => void;
}> = ({ label, field, className = "", sortKey, sortDir, onSort }) => (
  <th
    className={`px-[5px] cursor-pointer hover:opacity-60 select-none whitespace-nowrap ${className}`}
    style={{ paddingTop: 7, paddingBottom: 7 }}
    onClick={() => onSort(field)}
  >
    {label}
    {sortKey === field && <span className="ml-1">{sortDir === "asc" ? "\u25B2" : "\u25BC"}</span>}
  </th>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const InstallmentsTracker: React.FC<InstallmentsTrackerProps> = ({
  leads,
  snippet = false,
  timezone = "UTC",
  isAbandoned = false,
  onConversationClick,
  onAbandonClick,
  onRestoreClick,
  canAbandon = false,
  canRestore = false,
  inactiveRepIds,
  memberNameById,
  claimsByEmail,
  canClaimInstallment = false,
  onClaimInstallment,
  onReleaseInstallment,
  closedAfterDate = "",
  onClosedAfterChange,
  filterChips = [],
  onApplyFilter,
  onRemoveFilter,
  onClearColumnFilter,
  onClearAllFilters,
}) => {
  const { ref: revealRef, revealed } = useScrollReveal<HTMLDivElement>();
  const [sortKey, setSortKey] = useState<SortKey>("nextPayment");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [pageLimit, setPageLimit] = useState(PAGE_SIZE);
  const [timelineEmail, setTimelineEmail] = useState<string | null>(null);

  // ── Claim/release in-flight guard — synchronous per-email set so a rapid
  // double-click on the same row's button can't double-fire the RPC before
  // the first click's promise resolves and the button disables.
  const inFlightEmailsRef = useRef<Set<string>>(new Set());
  const [inFlightEmails, setInFlightEmails] = useState<Set<string>>(new Set());

  const handleClaimClick = useCallback(
    async (email: string) => {
      if (!onClaimInstallment || inFlightEmailsRef.current.has(email)) return;
      inFlightEmailsRef.current.add(email);
      setInFlightEmails(new Set(inFlightEmailsRef.current));
      try {
        await onClaimInstallment(email);
      } finally {
        inFlightEmailsRef.current.delete(email);
        setInFlightEmails(new Set(inFlightEmailsRef.current));
      }
    },
    [onClaimInstallment]
  );

  const handleReleaseClick = useCallback(
    async (email: string) => {
      if (!onReleaseInstallment || inFlightEmailsRef.current.has(email)) return;
      inFlightEmailsRef.current.add(email);
      setInFlightEmails(new Set(inFlightEmailsRef.current));
      try {
        await onReleaseInstallment(email);
      } finally {
        inFlightEmailsRef.current.delete(email);
        setInFlightEmails(new Set(inFlightEmailsRef.current));
      }
    },
    [onReleaseInstallment]
  );

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    [sortKey]
  );

  // ── Closed After filter (applied before sort) ──
  const closedAfterFiltered = useMemo(() => {
    if (!closedAfterDate) return leads;
    const cutoff = new Date(closedAfterDate + "T00:00:00");
    if (isNaN(cutoff.getTime())) return leads;
    return leads.filter((l) => {
      if (!l.closeDate) return false;
      return new Date(l.closeDate) >= cutoff;
    });
  }, [leads, closedAfterDate]);

  // ── Sort ──
  const sorted = useMemo(() => {
    return [...closedAfterFiltered].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      switch (sortKey) {
        case "name":
          return dir * a.leadName.localeCompare(b.leadName);
        case "program":
          return dir * (a.program || "").localeCompare(b.program || "");
        case "closer":
          return dir * (a.closerName || "").localeCompare(b.closerName || "");
        case "setter":
          return dir * (a.setterName || "").localeCompare(b.setterName || "");
        case "contract":
          return dir * (a.totalContract - b.totalContract);
        case "collected":
          return dir * (a.cashPaidSoFar - b.cashPaidSoFar);
        case "remaining":
          return (
            dir *
            (Math.max(0, a.totalContract - a.cashPaidSoFar) -
              Math.max(0, b.totalContract - b.cashPaidSoFar))
          );
        case "nextAmount":
          return dir * ((a.nextPaymentAmount || 0) - (b.nextPaymentAmount || 0));
        case "lastPayment":
          return (
            dir *
            (new Date(a.lastPaymentDate || 0).getTime() -
              new Date(b.lastPaymentDate || 0).getTime())
          );
        case "nextPayment": {
          if (!a.nextPaymentDate && !b.nextPaymentDate) return 0;
          if (!a.nextPaymentDate) return 1;
          if (!b.nextPaymentDate) return -1;
          return (
            dir * (new Date(a.nextPaymentDate).getTime() - new Date(b.nextPaymentDate).getTime())
          );
        }
        case "status": {
          const scores: Record<string, number> = {
            Overdue: 0,
            Active: 1,
            "No date": 2,
            Paid: 3,
            Abandoned: 4,
          };
          return dir * ((scores[getLeadStatus(a)] ?? 5) - (scores[getLeadStatus(b)] ?? 5));
        }
        default:
          return 0;
      }
    });
  }, [closedAfterFiltered, sortKey, sortDir]);

  // ── Pagination ──
  const displayed = snippet ? sorted.slice(0, 7) : sorted.slice(0, pageLimit);
  const hasMore = !snippet && pageLimit < sorted.length;

  // ── Payment activity sparkline (snippet mode only) ──
  const paymentActivity = useMemo(() => {
    if (!snippet || leads.length === 0) return [];
    const counts = new Map<string, number>();
    for (const l of leads) {
      const day = l.lastPaymentDate?.split("T")[0] || "";
      if (day) counts.set(day, (counts.get(day) || 0) + 1);
    }
    const sorted = [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return sorted.map(([, count]) => count);
  }, [snippet, leads]);

  // ── Click-to-filter handlers (event delegation on tbody) ──
  const handleCellClick = useCallback(
    (e: React.MouseEvent<HTMLTableSectionElement>) => {
      if (snippet || !onApplyFilter) return;
      const td = (e.target as HTMLElement).closest("td[data-filter-column]") as HTMLElement | null;
      if (!td) return;
      const column = td.dataset.filterColumn as InstallmentFilterColumn;
      const value = td.dataset.filterValue;
      if (!column || value === undefined || !FILTERABLE_COLUMNS.has(column)) return;
      onApplyFilter(column, value, "isolate");
    },
    [snippet, onApplyFilter]
  );

  const handleCellContextMenu = useCallback(
    (e: React.MouseEvent<HTMLTableSectionElement>) => {
      if (snippet || !onApplyFilter) return;
      const td = (e.target as HTMLElement).closest("td[data-filter-column]") as HTMLElement | null;
      if (!td) return;
      const column = td.dataset.filterColumn as InstallmentFilterColumn;
      const value = td.dataset.filterValue;
      if (!column || value === undefined || !FILTERABLE_COLUMNS.has(column)) return;
      e.preventDefault();
      onApplyFilter(column, value, "exclude");
    },
    [snippet, onApplyFilter]
  );

  // ── Helper: build filter data attributes for a cell ──
  const filterAttrs = useCallback(
    (column: InstallmentFilterColumn, lead: InstallmentLead) => {
      if (snippet) return {};
      if (!FILTERABLE_COLUMNS.has(column)) return {};
      return {
        "data-filter-column": column,
        "data-filter-value": getInstallmentColumnValue(lead, column),
        style: { cursor: "pointer" } as React.CSSProperties,
      };
    },
    [snippet]
  );

  // ── Reset page limit when leads change ──
  const leadCount = leads.length;
  React.useEffect(() => {
    setPageLimit(PAGE_SIZE);
  }, [leadCount]);

  // In snippet mode the outer liquid-glass card is provided by OperationsDashboard
  // (same pattern as TransactionLog). Skip inner Card to avoid double-wrapped glass.
  const content = (
    <>
      {/* Header — full mode only */}
      {!snippet && (
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2 relative z-20">
          <div>
            <h3 className="token-17 font-semibold text-glass mb-1">Installment Plans</h3>
            <p className="token-13 text-glass-secondary">
              {sorted.length} lead{sorted.length !== 1 ? "s" : ""}
              {closedAfterDate && (
                <span className="text-glass-muted">
                  {" "}
                  (closed after{" "}
                  {new Date(closedAfterDate + "T00:00:00").toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                  )
                </span>
              )}
            </p>
          </div>

          {/* Closed After picker */}
          {onClosedAfterChange && (
            <SingleDatePicker
              label="Closed after:"
              value={closedAfterDate}
              onChange={onClosedAfterChange}
              placeholder="Any time"
            />
          )}
        </div>
      )}

      {/* Filter bar — full mode only */}
      {!snippet && onRemoveFilter && onClearColumnFilter && onClearAllFilters && (
        <div className="mb-4">
          <FilterBar<InstallmentFilterColumn>
            chips={filterChips}
            onRemoveFilter={onRemoveFilter}
            onClearColumn={onClearColumnFilter}
            onClearAll={onClearAllFilters}
          />
        </div>
      )}

      {/* Filter hint — full mode, no active filters */}
      {!snippet && filterChips.length === 0 && (
        <p className="token-11 text-glass-muted mb-3">
          Left-click a cell to isolate · Right-click to exclude
        </p>
      )}

      {/* Table */}
      {displayed.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3 bg-black/[0.03] rounded-xl mx-0">
          <svg
            className="w-9 h-9 text-glass-muted opacity-40"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"
            />
          </svg>
          <p className="token-13 text-glass-muted">
            {leads.length === 0
              ? isAbandoned
                ? "No abandoned payment plans"
                : "No leads on payment plans yet"
              : "No leads match the current filters"}
          </p>
          {leads.length > 0 && (
            <p className="token-11 text-glass-inactive">
              Try adjusting the search or status filters above
            </p>
          )}
        </div>
      ) : (
        <div
          className={snippet ? "overflow-x-auto" : "overflow-auto -mx-5"}
          style={snippet ? undefined : { maxHeight: "70vh", overscrollBehavior: "contain" }}
        >
          <table className="w-full token-13">
            <thead className={snippet ? "" : "sticky top-0 z-10 bg-[#FAFBFC]"}>
              <tr
                className={
                  snippet
                    ? "border-b border-black/[0.06] text-left"
                    : "text-left token-11 text-black border-b border-black/[0.08]"
                }
              >
                {snippet ? (
                  <>
                    <th className="token-11 px-2 py-1.5 text-left">Lead</th>
                    <th className="token-11 px-2 py-1.5 text-left">Program</th>
                    <th className="token-11 px-2 py-1.5 text-right">Collected</th>
                    <th className="token-11 px-2 py-1.5 text-right">Remaining</th>
                    <th className="token-11 px-2 py-1.5 text-right">Next</th>
                  </>
                ) : (
                  <>
                    <SortHeader
                      label="Lead"
                      field="name"
                      className="pl-5"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <th className="px-[5px]" style={{ paddingTop: 7, paddingBottom: 7 }}>
                      Phone
                    </th>
                    <SortHeader
                      label="Program"
                      field="program"
                      className="min-w-[120px]"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Closer"
                      field="closer"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Setter"
                      field="setter"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Contract"
                      field="contract"
                      className="text-right"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Collected"
                      field="collected"
                      className="text-right"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Remaining"
                      field="remaining"
                      className="text-right"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Last Pmt"
                      field="lastPayment"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Next Pmt"
                      field="nextPayment"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Next Amt"
                      field="nextAmount"
                      className="text-right"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    <SortHeader
                      label="Status"
                      field="status"
                      className="text-center"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                    />
                    {(canAbandon || canRestore || canClaimInstallment) && (
                      <th
                        className="pl-2 pr-6 text-center w-14"
                        style={{ paddingTop: 7, paddingBottom: 7 }}
                      ></th>
                    )}
                  </>
                )}
              </tr>
            </thead>
            <tbody
              className="divide-y divide-black/[0.04]"
              onClick={handleCellClick}
              onContextMenu={handleCellContextMenu}
            >
              {displayed.map((lead, i) => {
                const remaining = Math.max(0, lead.totalContract - lead.cashPaidSoFar);
                const paidPercent =
                  lead.totalContract > 0
                    ? Math.min(100, (lead.cashPaidSoFar / lead.totalContract) * 100)
                    : 0;
                const status = getLeadStatus(lead);
                const isOverdue = status === "Overdue";
                const isMissingDate = status === "No date";
                const rowBg = isOverdue ? "bg-red-500/5" : isMissingDate ? "bg-amber-500/5" : "";

                if (snippet) {
                  return (
                    <tr
                      key={`${lead.leadEmail}-${i}`}
                      className="reveal-row"
                      style={{ animationDelay: `${i * 40}ms` }}
                    >
                      <td className="px-2 py-1.5 token-calendar">
                        <button
                          className="text-ds-accent hover:opacity-80 transition-colors cursor-pointer text-left"
                          onClick={() => lead.leadEmail && setTimelineEmail(lead.leadEmail)}
                        >
                          {toTitleCase(lead.leadName)}
                        </button>
                      </td>
                      <td className="px-2 py-1.5 token-calendar text-glass-secondary">
                        {lead.program || "--"}
                      </td>
                      <td className="px-2 py-1.5 text-right token-13m text-glass tabular-nums">
                        {formatUSD(lead.cashPaidSoFar)}
                      </td>
                      <td className="px-2 py-1.5 text-right token-13m text-glass tabular-nums">
                        {remaining > 0 ? formatUSD(remaining) : "--"}
                      </td>
                      <td
                        className={`px-2 py-1.5 text-right whitespace-nowrap token-calendar ${isOverdue ? "text-ds-red" : "text-glass"}`}
                      >
                        {formatDate(lead.nextPaymentDate, timezone)}
                      </td>
                    </tr>
                  );
                }

                // Claim pool — a plan is claimable when ORPHANED (closer is an inactive
                // rep) OR >=7 days OVERDUE (Mako 2026-07-17: closers rescue overdue
                // installments regardless of closer-active status). Mirrors the extended
                // claim_installment eligibility gate (sql/claim_installment.sql). ⚠️ the
                // Claim button 500s until the 2026-07-17 backend DDL is applied to prod.
                const claimInfo = claimsByEmail?.get(lead.leadEmail.toLowerCase());
                const isOrphaned = !!(lead.closerId && inactiveRepIds?.has(lead.closerId));
                const isOverdueClaimable = !!(lead.isOverdue && (lead.daysOverdue ?? 0) >= 7);
                const isClaimable = isOrphaned || isOverdueClaimable;

                return (
                  <tr
                    key={`${lead.leadEmail}-${i}`}
                    className={`hover:bg-black/[0.02] ${rowBg} anim-pop`}
                    style={{
                      animationDelay: `${Math.min(i, 15) * 100}ms`,
                      animationDuration: "480ms",
                    }}
                  >
                    {/* Lead name + email */}
                    <td className="px-[5px] py-1.5 pl-5">
                      <div className="flex items-center gap-1.5">
                        <span className="token-calendar font-medium text-glass">
                          {toTitleCase(lead.leadName)}
                        </span>
                        {lead.conversationId && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onConversationClick?.(
                                lead.conversationId!,
                                lead.leadName,
                                lead.leadInstagram || null
                              );
                            }}
                            className="text-pink-500 hover:text-pink-400 transition-colors"
                            title={`Open Instagram DM: ${lead.leadInstagram || "unknown"}`}
                          >
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                            </svg>
                          </button>
                        )}
                      </div>
                      <div className="token-8 text-glass-muted">{lead.leadEmail}</div>
                    </td>
                    {/* Phone */}
                    <td className="px-[5px] py-1.5 token-calendar text-glass-secondary whitespace-nowrap">
                      {lead.leadPhone || "--"}
                    </td>
                    {/* Program — filterable, 2-line clamp */}
                    <td
                      className="px-[5px] py-1.5 token-calendar text-glass-secondary min-w-[120px]"
                      {...filterAttrs("program", lead)}
                    >
                      <span className="line-clamp-2">{lead.program || "--"}</span>
                    </td>
                    {/* Closer — filterable */}
                    <td
                      className="px-[5px] py-1.5 token-calendar text-glass-secondary"
                      {...filterAttrs("closer", lead)}
                    >
                      {lead.closerName || "--"}
                      {lead.closerId && inactiveRepIds?.has(lead.closerId) && (
                        <span className="token-9 uppercase tracking-wide text-ds-amber ml-1.5 px-1.5 py-0.5 rounded bg-black/[0.04]">
                          inactive
                        </span>
                      )}
                    </td>
                    {/* Setter — filterable */}
                    <td
                      className="px-[5px] py-1.5 token-calendar text-glass-secondary"
                      {...filterAttrs("setter", lead)}
                    >
                      {lead.setterName || "--"}
                    </td>
                    {/* Contract value */}
                    <td className="px-[5px] py-1.5 token-13m text-right font-medium text-glass align-top">
                      {lead.totalContract > 0 ? formatUSD(lead.totalContract) : "--"}
                    </td>
                    {/* Collected + progress bar */}
                    <td className="px-[5px] py-1.5 text-right align-top">
                      <span className="token-13m font-medium text-ds-accent">
                        {formatUSD(lead.cashPaidSoFar)}
                      </span>
                      {lead.totalContract > 0 && (
                        <div className="w-full h-[2px] mt-px rounded-full bg-black/[0.08]">
                          <div
                            className="h-full rounded-full bg-ds-accent"
                            style={{ width: `${paidPercent}%` }}
                          />
                        </div>
                      )}
                    </td>
                    {/* Remaining */}
                    <td className="px-[5px] py-1.5 token-13m text-right font-medium text-ds-amber align-top">
                      {remaining > 0 ? formatUSD(remaining) : "--"}
                    </td>
                    {/* Last payment */}
                    <td className="px-[5px] py-1.5 token-calendar text-glass-secondary whitespace-nowrap">
                      {formatDate(lead.lastPaymentDate, timezone)}
                    </td>
                    {/* Next payment date */}
                    <td
                      className={`px-[5px] py-1.5 token-calendar whitespace-nowrap ${
                        isOverdue ? "text-ds-red font-medium" : "text-glass-secondary"
                      }`}
                    >
                      {formatDate(lead.nextPaymentDate, timezone)}
                      {isOverdue && lead.daysOverdue != null && lead.daysOverdue > 0 && (
                        <span className="token-11 ml-2 text-ds-red">{lead.daysOverdue}d</span>
                      )}
                    </td>
                    {/* Next payment amount */}
                    <td className="px-[5px] py-1.5 text-right token-13m text-glass-secondary">
                      {lead.nextPaymentAmount ? formatUSD(lead.nextPaymentAmount) : "--"}
                    </td>
                    {/* Status — filterable */}
                    <td className="px-[5px] py-1.5 text-center" {...filterAttrs("status", lead)}>
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded-full token-11 font-medium ${statusBadge(status)}`}
                      >
                        {status}
                      </span>
                    </td>
                    {/* Abandon/Restore + claim pool — merged into one cell so per-row
                        conditional content (claim state varies row-to-row) never
                        misaligns the column against the header <th>. */}
                    {(canAbandon || canRestore || canClaimInstallment) && (
                      <td className="pl-2 pr-6 py-1.5 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          {/* Abandon (active tab) or Restore (abandoned tab) */}
                          {canAbandon && !isAbandoned && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onAbandonClick?.(lead);
                              }}
                              className="text-glass-muted hover:text-ds-red transition-colors"
                              title="Mark as abandoned"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={1.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                                />
                              </svg>
                            </button>
                          )}
                          {canRestore && isAbandoned && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onRestoreClick?.(lead);
                              }}
                              className="text-glass-muted hover:text-ds-green transition-colors"
                              title="Restore to active"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={1.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"
                                />
                              </svg>
                            </button>
                          )}
                          {/* Claim pool — orphaned plan (closer inactive) self-claim.
                              Abandoned (written-off) plans are never claimable — gate
                              on the same isAbandoned flag the restore button uses. */}
                          {!isAbandoned &&
                            (claimInfo ? (
                              <>
                                <span className="token-9 text-glass-muted whitespace-nowrap">
                                  Claimed &middot;{" "}
                                  {memberNameById?.get(claimInfo.collectorId) ?? "rep"}
                                </span>
                                {canClaimInstallment && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleReleaseClick(lead.leadEmail);
                                    }}
                                    disabled={inFlightEmails.has(lead.leadEmail)}
                                    className="token-11 font-medium px-2 py-1 rounded bg-black/[0.04] hover:bg-black/[0.08] text-glass-secondary anim-tap disabled:opacity-50"
                                  >
                                    Release
                                  </button>
                                )}
                              </>
                            ) : (
                              isClaimable &&
                              canClaimInstallment && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleClaimClick(lead.leadEmail);
                                  }}
                                  disabled={inFlightEmails.has(lead.leadEmail)}
                                  className="token-11 font-medium px-2 py-1 rounded bg-black/[0.04] hover:bg-black/[0.08] text-glass-secondary anim-tap disabled:opacity-50"
                                >
                                  Claim
                                </button>
                              )
                            ))}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination — full mode only */}
      {!snippet && sorted.length > 0 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-black/[0.06]">
          <span className="token-11 text-glass-muted">
            Showing {Math.min(pageLimit, sorted.length)} of {sorted.length}
          </span>
          {hasMore && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPageLimit((p) => p + PAGE_SIZE)}
                className="token-11 font-medium text-ds-accent hover:opacity-80 transition-colors anim-tap"
              >
                Load {Math.min(PAGE_SIZE, sorted.length - pageLimit)} more
              </button>
              <span className="text-black/20">|</span>
              <button
                onClick={() => setPageLimit(sorted.length)}
                className="token-11 font-medium text-glass-muted hover:text-glass-secondary transition-colors anim-tap"
              >
                Load all
              </button>
            </div>
          )}
        </div>
      )}

      {/* Abandoned lead details — show reason/notes inline */}
      {isAbandoned && !snippet && displayed.length > 0 && (
        <div className="mt-2 token-11 text-glass-muted">Click a lead row for abandon details</div>
      )}

      {/* ── Payment activity sparkline (snippet mode) ── */}
      {snippet && paymentActivity.length > 2 && (
        <div className="mt-2 flex items-center justify-between px-1">
          <span className="token-9 uppercase tracking-wide text-glass-muted">Payment activity</span>
          <MiniSparkline
            data={paymentActivity}
            width={100}
            height={22}
            color="rgba(59, 130, 246, 0.12)"
            strokeColor="rgba(59, 130, 246, 0.5)"
          />
        </div>
      )}

      {/* View all link moved to SectionHeader badge area in OperationsDashboard */}

      {/* Lead timeline modal */}
      {timelineEmail && (
        <LeadTimelineModal
          isOpen={!!timelineEmail}
          email={timelineEmail}
          onClose={() => setTimelineEmail(null)}
        />
      )}
    </>
  );

  // In snippet mode, return content directly (parent provides liquid-glass card).
  // In full mode, wrap in glass-dense card (same pattern as TransactionLog).
  return snippet ? (
    <div ref={revealRef} className={revealed ? "reveal-active" : "reveal-pending"}>
      {content}
    </div>
  ) : (
    <div className="liquid-glass anim-lift glass-dense rounded-[20px] p-5 overflow-hidden">
      {/* relative z-[2] lifts content above liquid-glass ::after (z-index: 1) sheen layer */}
      <div className="relative z-[2]">{content}</div>
    </div>
  );
};

export default InstallmentsTracker;
